import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, CardContent, Modal } from '@mui/material';
import MainCard from 'components/MainCard';
import { equipmentISOStatus, getEquipmentStatusColorByStatus, markerColorByStatus } from 'data/data';
import { getEquipmentLocationMapByMapId } from 'components/axios/equipmentZoneAxios';
import { useEnumApi } from 'contexts/EnumApiContext';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import { getEquipmentsById } from 'components/axios/equipmentAxios';

const TransparentTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    padding: 0,
  },
}));

export function CompanyImageViewerLayer({fileUrl, mapsId}) {
  const { getEnumValueByEnumNameAndCode } = useEnumApi();
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
  }
  const handleClose = () => setOpen(false);
  
  const [markers, setMarkers] = useState([]); // marker infos(id, position)
  const [equipmentMappingList, setEquipmentMappingList] = useState(null);
  // 1. 이미지 원본 비율 저장할 state 추가
  const [aspectRatio, setAspectRatio] = useState(null);

  const handleImageLoad = (e) => {
    const { naturalWidth, naturalHeight } = e.target;
    if (naturalWidth && naturalHeight) {
      setAspectRatio(naturalWidth / naturalHeight);
    }
  };
  
  useEffect(()=>{
    if (open) {
      getEquipmentLocationMapByMapId(mapsId)
      .then(async (res) => {
        if (!res || !res.coordinates) {
          setEquipmentMappingList(null);
          setMarkers([]);
          return;
        }

        // 1. 기본 좌표 정보 및 mId 생성
        const mappingList = res.coordinates.map((m) => ({
          ...m,
          mId: `marker-${Math.floor(Math.random() * 10000)}`
        }));
        setEquipmentMappingList(mappingList);

        // 2. 각 설비의 상세 정보(getEquipmentById) 병렬 호출
        const updatedMarkers = await Promise.all(
          mappingList.map(async (m) => {
            let fullEquipment = m.equipment;

            // 설비 ID가 존재하는 경우 상세 정보 API 호출
            if (m.equipment?.id) {
              try {
                const detail = await getEquipmentsById(m.equipment.id);
                fullEquipment = { ...m.equipment, ...detail };
              } catch (err) {
                console.error(`Equipment ID ${m.equipment.id} 조회 실패`, err);
              }
            }

            return {
              id: m.mId,
              equipment: fullEquipment,
              position: { x: m.yawOrX, y: m.pitchOrY },
              status: fullEquipment?.status
            };
          })
        );

        // 3. 완성된 마커 데이터 저장
        // console.log("상세 정보가 포함된 마커 목록:", updatedMarkers);
        setMarkers(updatedMarkers);
      })
      .catch((err) => console.error("Map Data Loading error:", err));
    }
  }, [open])

  return (
      <>
        <img
          src={fileUrl}
          alt="Thumbnail"
          // className="w-32 h-32 object-cover cursor-pointer rounded"
          onClick={handleOpen} 
          style={{width: "80px" ,height: "80px" ,padding:"3px", cursor:'pointer'}}
        />
  
        <Modal className="pop_box" open={open} onClose={handleClose} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
          <MainCard style={{maxWidth:"1280px", width:"90%", maxHeight:"75vh"}} title="" modal darkTitle content={false}>
            <CardContent style={{height:"100%"}}>
              <div className='pop_close'>
                <Button color="primary" size="small" onClick={handleClose}>
                  {t('message.common.btn.close')}
                </Button>
              </div>
  
              {/* 2. 스크롤을 방지하고 이미지를 부모 영역에 맞추는 컨테이너 */}
              <div style={{
                width: "100%", 
                height: '55vh', 
                overflow: "hidden", 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center"
              }}>
                {/* 3. 이미지 원본 비율로 구속되는 마커 감싸기 래퍼 */}
                <div
                  style={{ 
                    position: "relative", 
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    maxWidth: '100%', 
                    maxHeight: '100%',
                    aspectRatio: aspectRatio ? `${aspectRatio}` : "auto"
                  }}
                  // onClick={handleImageClick}
                >
                  <img
                    src={fileUrl}
                    alt="image"
                    onLoad={handleImageLoad}
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "block",
                      objectFit: "contain"
                    }}
                  />
                  {markers.map((m, mIdx) => {
                    const statusColor = getEquipmentStatusColorByStatus(m.equipment?.status);
                    const isoStatus = equipmentISOStatus.find((iso) => iso.code === m.equipment?.lowestDiagnosisRank);
                    const isoColor = isoStatus?.color3 || '#62b929';
                    const isoCode = (isoStatus || { code: "-" }).code;

                    return (
                    <TransparentTooltip
                      key={m.id}
                      title={
                        <React.Fragment>
                          <div className="map_pin"
                            style={{
                              width: "350px",
                              height: "150px",
                              padding: "20px",
                              background: "unset"
                            }}
                          >
                            <div style={{
                              width: "300px",
                              padding: "12px 16px",
                              borderRadius: "14px",
                              color: "#fff",
                              fontSize: "13px",
                              display: "flex",
                              flexDirection: "column",
                              border: "1px solid rgba(0, 0, 0, 0.25)",
                              gap: "10px" }}>
                            {/* 1. 상단 타이틀 바 */}
                              <div
                                style={{
                                  borderRadius: "16px",
                                  width: "100%",
                                  background: statusColor?.bg2 ?? "#dddddd",
                                  padding: "6px",
                                  textAlign: "center",
                                  fontWeight: "bold",
                                  fontSize: "13px",
                                  color: statusColor?.color ?? '#fff',
                                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                                }}
                              >
                                {m.equipment ? `${m.equipment.name} (${m.equipment.equipmentNo})` : t('message.common.text.Unmapped')}
                              </div>

                              {/* 2. 상세 항목 리스트 */}
                              <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "4px" }}>
                                
                                {/* 설비 분류 */}
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                  <span style={{ color: "#fff", fontSize: "12px" }}>{t("message.common.subtitle.equipment.level3")}</span>
                                  <span style={{ fontWeight: "500", fontSize: "11px" }}>{m.equipment?.equipmentCtgr?.name ?? "-"}</span>
                                </div>

                                {/* 상태 뱃지 */}
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                  <span style={{ color: "#fff", fontSize: "12px" }}>{t("message.common.subtitle.status")}</span>
                                  <span
                                    style={{
                                      background: statusColor?.bg2 ?? "#dddddd",
                                      color: statusColor?.color ?? '#fff',
                                      padding: "2px 10px",
                                      borderRadius: "12px",
                                      fontSize: "11px",
                                      fontWeight: "bold"
                                    }}
                                  >
                                    {getEnumValueByEnumNameAndCode("EquipmentFaultStatus", m.equipment?.status)?.[0]?.title ?? "-"}
                                  </span>
                                </div>

                                {/* 운행 정보 */}
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                  <span style={{ color: "#fff", fontSize: "12px" }}>{t("message.common.subtitle.operation.info")}</span>
                                  <span
                                    style={{
                                      background: m.equipment?.latestEquipmentOp?.opStatus?.toUpperCase() === "ACTIVE" ? "#2672f7" : "#777",
                                      padding: "2px 10px",
                                      borderRadius: "12px",
                                      fontSize: "11px"
                                    }}
                                  >
                                    {m.equipment?.latestEquipmentOp?.opStatus?.toUpperCase() === "ACTIVE" ? t('message.common.text.active') : t('message.common.text.stop')}
                                  </span>
                                </div>

                                {/* ISO 등급 또는 추가 항목 */}
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                  <span style={{ color: "#fff", fontSize: "12px" }}>{t('message.common.subtitle.iso')} {t("message.common.subtitle.grade")}</span>
                                  <span
                                    style={{
                                      background: isoColor,
                                      color: m.equipment?.lowestDiagnosisRank === "C" ? "#000":"#fff",
                                      width: "20px",
                                      height: "20px",
                                      borderRadius: "50%",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      fontSize: "11px",
                                      fontWeight: "bold"
                                    }}
                                  >
                                    {isoCode}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </React.Fragment>
                      }
                      // arrow
                      placement="top"
                    >
                    <img
                      key={m.id}
                      src={markerColorByStatus[m.equipment?.status || "unmapped"]}
                      style={{
                        position: "absolute",
                        top: `${m.position.y * 100}%`,
                        left: `${m.position.x * 100}%`,
                        width: 32,
                        height: 32,
                        transform: "translate(-50%, -50%)",
                      }}
                    />
                    </TransparentTooltip>
                    )
                  })}
                </div>
              </div>
            </CardContent>
  
          </MainCard>
        </Modal>

      </>
  )
}

export default CompanyImageViewerLayer;
  